// -*- coding: utf-8 -*-
/**
 * Types for Stage 069 Task Dispatcher & Worker Cockpit.
 * Governance Anchor: TASK-MARKET_HUB-069-TASK-HARMONIZER-AND-WORKER-COCKPIT
 * Architecture Reference: docs/20-architecture/ADR-069-TASK-HARMONIZER-AND-WORKER-TELEMETRY.md
 */

export type WorkerDomain = 'CRYPTO' | 'ASHARE' | 'MACRO' | 'INFRA';

export type WorkerRunStatus = 'SUCCESS' | 'SKIPPED' | 'FAILED' | 'UNAVAILABLE' | 'UNKNOWN';

export type DispatcherTabId = 'GLOBAL' | 'CRYPTO' | 'ASHARE' | 'MACRO' | 'INFRA';

export type CockpitFreshnessState = 'FRESH' | 'STALE' | 'DEGRADED' | 'UNHEALTHY' | 'UNAVAILABLE' | 'UNKNOWN';

export interface WorkerTelemetryItem {
  name: string;
  domain: WorkerDomain | string;
  period_seconds: number;
  last_run_utc: string | null;
  last_completed_utc: string | null;
  last_duration_ms: number | null;
  status: WorkerRunStatus | string;
  consecutive_errors: number;
  last_error_category: string | null;
  last_skipped_utc: string | null;
  last_skip_reason: string | null;
  source_type: 'LOCAL_LOOP' | 'SYSTEMD_TIMER' | string;
  is_active: boolean;
}

export interface WorkerTelemetrySnapshot {
  observed_at_utc: string;
  instance_id: string;
  deployment_invariant: string;
  measured_event_loop_lag_ms: number | null;
  active_supervised_count: number;
  total_registered_count: number;
  workers: WorkerTelemetryItem[];
}

export interface WorkerMetaInfo {
  label: string;
  sub: string;
  icon: string;
  periodDisplay: string;
  expectedLagWindow?: string;
}

export const WORKER_METADATA_MAP: Record<string, WorkerMetaInfo> = {
  indicator_materializer: {
    label: '多周期指标计算引擎',
    sub: 'Crypto Indicators 30s Materializer',
    icon: '📊',
    periodDisplay: '30s',
    expectedLagWindow: 'W >= 3s',
  },
  trend_weather_worker: {
    label: '7x7 趋势气象工人',
    sub: 'Crypto Trend Weather 60s Copilot',
    icon: '🌤️',
    periodDisplay: '60s',
    expectedLagWindow: 'W >= 3s',
  },
  collector_daemon: {
    label: '高频数据采集守护进程',
    sub: '5s Ingestion Worker & Resampler',
    icon: '⚡',
    periodDisplay: '5s',
    expectedLagWindow: 'Tick <= 5s',
  },
  multiasset_ingress_daemon: {
    label: '多资产行情网关守护',
    sub: 'Multi-Asset Gate & Kill Switch Mesh',
    icon: '📡',
    periodDisplay: '1s',
  },
  grounding_observer: {
    label: '全息系统接地对齐探针',
    sub: 'Grounding & SSOT Topology Observer',
    icon: '🧭',
    periodDisplay: '600s (10m)',
  },
  macro_market_worker: {
    label: '15类大宗宏观资产工人',
    sub: 'Macro Quotes & Corridor Sync (5s)',
    icon: '🌐',
    periodDisplay: '5s',
  },
  watchlist_intraday_worker: {
    label: '核心自选 1m 盘中重采样',
    sub: 'Watchlist Paced Ingress Resampler',
    icon: '📈',
    periodDisplay: '60s',
  },
  ashare_eod_ingress: {
    label: 'A股日线盘后入库工人',
    sub: 'A-Share EOD Historical Pipeline',
    icon: '🇨🇳',
    periodDisplay: '1d (86400s)',
  },
};
